'use client'

import Link from 'next/link'
import React, { useState } from 'react'
import { FaLinkedinIn, FaFacebookF, FaYoutube, FaChevronDown, FaChevronUp, FaGithub } from 'react-icons/fa'
import { FaXTwitter, FaBluesky } from 'react-icons/fa6'
import { RiInstagramFill } from 'react-icons/ri'
import { IconType } from 'react-icons'

import Image from 'next/image'
import type { Footer } from '@/payload-types'
import { ComplianceBanner } from './ComplianceBanner'

// Social platform to icon mapping
const platformIcons: Record<string, IconType> = {
  instagram: RiInstagramFill,
  linkedin: FaLinkedinIn,
  x: FaXTwitter,
  facebook: FaFacebookF,
  youtube: FaYoutube,
  github: FaGithub,
  bluesky: FaBluesky,
}

// Accordion Section Component for Mobile
const AccordionSection = ({
  title,
  children,
  isOpen,
  onToggle,
}: {
  title: string
  children: React.ReactNode
  isOpen: boolean
  onToggle: () => void
}) => {
  return (
    <div className={`flex flex-col items-start flex-1 ${isOpen ? 'gap-3' : ''}`}>
      <button
        onClick={onToggle}
        className="flex items-center justify-between w-full text-left"
        aria-expanded={isOpen}
      >
        <h3 className="footer-heading text-black">{title}</h3>
        {isOpen ? (
          <FaChevronUp className="text-black w-4 h-4" />
        ) : (
          <FaChevronDown className="text-black w-4 h-4" />
        )}
      </button>
      {isOpen && <div className="flex flex-col gap-2">{children}</div>}
      {/* Horizontal divider - always visible on mobile/tablet */}
      <div className="w-full h-px bg-black mt-4"></div>
    </div>
  )
}

// Social Links Component
const SocialLinks = ({
  socialLinks,
  className = '',
}: {
  socialLinks: NonNullable<Footer['socialLinks']>
  className?: string
}) => {
  return (
    <div className={`flex gap-3 items-center ${className}`}>
      {socialLinks.map((social, index) => {
        const IconComponent = platformIcons[social.platform] || FaLinkedinIn
        return (
          <a
            key={social.id || index}
            href={social.url}
            className="w-8 h-8 border border-black rounded-full flex items-center justify-center hover:bg-black group transition-all ease-in-out"
            target="_blank"
            rel="noopener noreferrer"
          >
            <IconComponent className="text-black w-auto h-3 group-hover:text-civo-lime transition-colors" />
          </a>
        )
      })}
    </div>
  )
}

// Helper to get link href from Payload link field
function getLinkHref(link: {
  type?: ('reference' | 'custom' | 'chat' | 'jumpToBlock') | null
  reference?:
    | {
        relationTo: 'pages' | 'posts' | 'webinars' | 'platform-engineering-unplugged'
        value: string | { slug?: string | null }
      }
    | null
  url?: string | null
  anchorId?: string | null
}): string {
  if (link.type === 'reference' && link.reference) {
    const value = link.reference.value
    if (typeof value === 'string') {
      return `/${value}`
    }
    return value?.slug ? `/${value.slug}` : '#'
  }
  if (link.type === 'custom' && link.url) {
    return link.url
  }
  if (link.type === 'jumpToBlock' && link.anchorId) {
    return `#${link.anchorId}`
  }
  return '#'
}

// Check if link is external
function isExternalLink(href: string): boolean {
  return href.startsWith('http://') || href.startsWith('https://')
}

interface FooterClientProps {
  data: Footer
}

export function FooterClient({ data }: FooterClientProps) {
  const sections = data.sections ?? []
  const socialLinks = data.socialLinks ?? []
  const legalLinks = data.legalLinks ?? []

  // Dynamic accordion state based on number of sections
  const [openSections, setOpenSections] = useState<Record<number, boolean>>(
    sections.reduce(
      (acc, _, index) => {
        acc[index] = false
        return acc
      },
      {} as Record<number, boolean>,
    ),
  )

  const toggleSection = (index: number) => {
    setOpenSections((prev) => ({
      ...prev,
      [index]: !prev[index],
    }))
  }

  const currentYear = new Date().getFullYear()

  return (
    <footer className="relative z-10">
      {/* Compliance Banner */}
      <ComplianceBanner />

      {/* Main Footer */}
      <div className="bg-civo-lime">
        <div className="px-7 py-11 md:px-9 xl:px-16 lg:pt-20 lg:pb-7">
          <div className="max-w-[1296px] mx-auto">
            <div className="flex flex-col gap-[35px] lg:gap-[100px]">
              {/* Mobile: Logo + Social Icons + Divider Section */}
              <div className="flex flex-col gap-3 items-start w-full md:hidden">
                {/* Mobile: Logo below social, Tablet+: Logo left, social right */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between w-full gap-3 md:gap-0">
                  <Image
                    src="/assets-rebrand/logos/civo-black.svg"
                    alt="Civo"
                    width={84}
                    height={31}
                    className="max-w-[84px] h-auto"
                  />
                </div>
                <div className="w-full h-px bg-black"></div>
              </div>

              {/* Navigation Section - Responsive */}
              <div className="flex flex-col gap-12 md:gap-7">
                {/* Desktop Logo and Social Icons */}
                <div className="hidden md:flex items-center justify-between w-full">
                  <Image
                    src="/assets-rebrand/logos/civo-black.svg"
                    alt="Civo"
                    width={128}
                    height={50}
                    className="max-w-[130px] h-auto"
                  />
                  {socialLinks.length > 0 && <SocialLinks socialLinks={socialLinks} className="flex" />}
                </div>

                {/* Desktop Divider */}
                <div className="hidden md:block w-full h-px bg-black"></div>

                {/* Navigation Grid - Mobile: 2 cols, MD: 3 cols, Desktop: 4 cols */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-8 xl:gap-x-16 xl:gap-y-12 w-full">
                  {sections.map((section, sectionIndex) => (
                    <div key={section.id || sectionIndex} className="flex flex-col gap-8 items-start flex-1">
                      {/* Mobile/Tablet: Accordion */}
                      <div className="lg:hidden w-full">
                        <AccordionSection
                          title={section.title}
                          isOpen={openSections[sectionIndex] || false}
                          onToggle={() => toggleSection(sectionIndex)}
                        >
                          {section.links?.map((linkItem, linkIndex) => {
                            const href = getLinkHref(linkItem.link)
                            const isExternal = isExternalLink(href)

                            if (isExternal) {
                              return (
                                <a
                                  key={linkItem.id || linkIndex}
                                  href={href}
                                  className="footer-link text-black opacity-80 hover:opacity-100"
                                  target={linkItem.link.newTab ? '_blank' : undefined}
                                  rel={linkItem.link.newTab ? 'noopener noreferrer' : undefined}
                                >
                                  {linkItem.link.label}
                                </a>
                              )
                            }

                            return (
                              <Link
                                key={linkItem.id || linkIndex}
                                href={href}
                                className="footer-link text-black opacity-80 hover:opacity-100"
                              >
                                {linkItem.link.label}
                              </Link>
                            )
                          })}
                        </AccordionSection>
                      </div>

                      {/* Desktop: Full Display */}
                      <div className="hidden lg:flex lg:flex-col lg:gap-8 lg:items-start lg:flex-1">
                        <h3 className="footer-heading text-black">{section.title}</h3>
                        <div className="flex flex-col gap-3">
                          {section.links?.map((linkItem, linkIndex) => {
                            const href = getLinkHref(linkItem.link)
                            const isExternal = isExternalLink(href)

                            if (isExternal) {
                              return (
                                <a
                                  key={linkItem.id || linkIndex}
                                  href={href}
                                  className="footer-link text-black opacity-80 hover:opacity-100"
                                  target={linkItem.link.newTab ? '_blank' : undefined}
                                  rel={linkItem.link.newTab ? 'noopener noreferrer' : undefined}
                                >
                                  {linkItem.link.label}
                                </a>
                              )
                            }

                            return (
                              <Link
                                key={linkItem.id || linkIndex}
                                href={href}
                                className="footer-link text-black opacity-80 hover:opacity-100"
                              >
                                {linkItem.link.label}
                              </Link>
                            )
                          })}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Copyright */}
              <div className="footer-copyright text-black">
                {/* Mobile: 2 column layout */}
                <div className="grid grid-cols-2 gap-4 md:hidden">
                  <ul className="flex flex-col gap-2">
                    <h3 className="footer-heading text-black">Legal</h3>
                    <li>Copyright &copy; {currentYear}</li>
                    {legalLinks.map((legalItem, index) => {
                      const href = getLinkHref(legalItem.link)
                      const isExternal = isExternalLink(href)

                      if (isExternal) {
                        return (
                          <li key={legalItem.id || index}>
                            <a
                              href={href}
                              className="hover:underline"
                              target={legalItem.link.newTab ? '_blank' : undefined}
                              rel={legalItem.link.newTab ? 'noopener noreferrer' : undefined}
                            >
                              {legalItem.link.label}
                            </a>
                          </li>
                        )
                      }

                      return (
                        <li key={legalItem.id || index}>
                          <Link href={href} className="hover:underline">
                            {legalItem.link.label}
                          </Link>
                        </li>
                      )
                    })}
                  </ul>
                  <ul className="flex flex-col gap-2">
                    <h3 className="footer-heading text-black">Social</h3>
                    {socialLinks.map((social, index) => (
                      <li key={social.id || index}>
                        <a
                          href={social.url}
                          className="hover:underline"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {social.platform === 'x' ? 'X' : social.platform.charAt(0).toUpperCase() + social.platform.slice(1)}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
                {/* Desktop: single row */}
                <div className="hidden md:flex justify-between items-center">
                  <ul className="flex flex-wrap items-center gap-2">
                    <li className="mr-2">Copyright &copy; {currentYear}</li>
                    {legalLinks.map((legalItem, index) => {
                      const href = getLinkHref(legalItem.link)
                      const isExternal = isExternalLink(href)

                      return (
                        <React.Fragment key={legalItem.id || index}>
                          {isExternal ? (
                            <li>
                              <a
                                href={href}
                                className="hover:underline"
                                target={legalItem.link.newTab ? '_blank' : undefined}
                                rel={legalItem.link.newTab ? 'noopener noreferrer' : undefined}
                              >
                                {legalItem.link.label}
                              </a>
                            </li>
                          ) : (
                            <li>
                              <Link href={href} className="hover:underline">
                                {legalItem.link.label}
                              </Link>
                            </li>
                          )}
                          {index < legalLinks.length - 1 && <li>|</li>}
                        </React.Fragment>
                      )
                    })}
                  </ul>
                  <a
                    href="#top"
                    onClick={(e) => {
                      e.preventDefault()
                      window.scrollTo({ top: 0, behavior: 'smooth' })
                    }}
                    className="flex items-center gap-4 text-black group"
                  >
                    <span className="footer-link flex-shrink-0">Back to top</span>
                    <div className="w-10 h-10 border border-black rounded-full flex items-center justify-center group-hover:bg-black transition-all ease-in-out flex-shrink-0">
                      <svg
                        className="group-hover:text-civo-lime transition-colors"
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g clipPath="url(#clip0_4354_4963)">
                          <path
                            d="M8.00016 11.3335L8.00016 5.50016M8.00016 5.50016L5.0835 8.41683M8.00016 5.50016L10.9168 8.41683"
                            stroke="currentColor"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_4354_4963">
                            <rect width="16" height="16" fill="white" transform="translate(0 16) rotate(-90)" />
                          </clipPath>
                        </defs>
                      </svg>
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}
